/* React hooks */
import { useEffect, useState } from "react";
/* Routing */
import { useLocation, useParams } from "react-router-dom";
/* Contexts */
import useAuth from "../../hooks/useAuth";
import { updateDeceasedGeneralInfos, getDeceasedInfos } from "../../api/pro/deceasedReq";
/* Components */
import useData from "../../hooks/useData";
import useDeceased from "../../hooks/useDeceased";
import CivilityForm from "../../components/common/input-layout/CivilityForm";
import LastNameForm from "../../components/common/input-layout/LastNameForm";
import FirstnameForm from "../../components/common/input-layout/FirstnameForm";
import InputFullAddress from "../../components/common/input-layout/InputFullAddress";
import FormButtons from "../../components/common/FormButtons";
import jwt_decode from "jwt-decode";
import {
    checkRequiredFields,
    getErrorListFromValidation,
    patternListChoices,
    removeErrorList,
    validateFormInput,
    fileToBase64, normalizeZipCode
} from "../../helper/tools";
import { getDefaultImageLibrary } from "../../api/admin/imageReq";
import { getAgencyDefaultDeceasedImage } from "../../api/pro/agencyReq";

export default function FormGeneralInfos({
    className,
    legend,
    setShowModal = () => { },
    apiDeceasedInfos,
    data,
    hideButtons = false,
}) {
    const { id } = useParams();
    const { auth } = useAuth();
    const token = auth.token;
    const decodedToken = jwt_decode(token);
    const agencyId = decodedToken.agency;
    const [values, setValues] = useState(data || {});
    const { titleList, setTitleList, deceasedInfos, setDeceasedInfos } = useDeceased({});
    const { selectedCity, setSelectedCity, imageLib, setImageLib, agencyDefaultDeceasedImage, setAgencyDefaultDeceasedImage } = useData({});
    const { pathname } = useLocation();
    const { newForm, setNewForm } = useDeceased({});
    const [selectedPortrait, setSelectedPortrait] = useState();
    const [selectedImage, setSelectedImage] = useState();
    const [combinedImageList, setCombinedImageList] = useState([]);
    const [showPortraitModal, setShowPortraitModal] = useState(false);
    const [selectedCompressedFile, setSelectedCompressedFile] = useState();
    const [fileName, setFileName] = useState();
    const [useDefaultPortrait, setUseDefaultPortrait] = useState(true);
    const IMG_FOLDER = process.env.REACT_APP_LOCAL_ASSETS_BASE_URL;

    const apiImageLib = async () => {
        const response = await getDefaultImageLibrary(token)
        setImageLib(response)
    };

    const apiDeceasedInfosLocal = async () => {
        if (id) {
            const response = await getDeceasedInfos(token, id)
            setDeceasedInfos(response)
        }
    };

    const apiDefaultImage = async () => {
        const response = await getAgencyDefaultDeceasedImage(token, agencyId)
        let data;
        if (Array.isArray(response)) {
            data = response.length > 0 ? response[0] : {};
        } else {
            data = response || {};
        }

        if (data && !data.defaultImage) {
            if (data.image) {
                data = { ...data, defaultImage: data.image };
            } else if (data.default_image) {
                data = { ...data, defaultImage: data.default_image };
            }
        }

        setAgencyDefaultDeceasedImage(data);
    };

    // Bloquer le scroll du body quand la modal est ouverte
    useEffect(() => {
        if (showPortraitModal) {
            document.body.classList.add('modal-open');
        } else {
            document.body.classList.remove('modal-open');
        }

        // Cleanup: retirer la classe quand le composant est démonté
        return () => {
            document.body.classList.remove('modal-open');
        };
    }, [showPortraitModal]);

    useEffect(() => {
        if (newForm !== undefined && Object.keys(newForm).length > 0) {
            setValues(newForm);
        } else if (pathname?.includes("nouveau")) {
            // newForm vide (nouvelle création) : repartir de zéro plutôt que de fusionner sur un
            // état local potentiellement périmé (défunt précédent, tentative abandonnée...).
            setValues({ isListed: 1 });
        }
    }, [newForm]);

    useEffect(() => {
        apiImageLib()
        apiDeceasedInfosLocal()
        apiDefaultImage()
    }, [token, agencyId]);

    // setNewForm (état partagé via le contexte) n'est volontairement PAS appelé depuis l'intérieur
    // du updater de setValues : React peut invoquer un updater plusieurs fois, et comme setNewForm
    // redéclenche à son tour l'effet "newForm → setValues(newForm)" ci-dessous, les deux finissaient
    // par s'entretenir en boucle ("Maximum update depth exceeded") en tapant dans Nom/Prénom.
    const onChange = (e) => {
        const newValues = { ...values, [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value };
        setValues(newValues);

        if (pathname?.includes("nouveau")) {
            setNewForm(prevForm => ({ ...prevForm, ...newValues }));
        }

        validateFormInput(e);
    };

    useEffect(() => {
        if (selectedCity?.city_zip) {
            // Vérifier si les valeurs ont vraiment changé pour éviter une boucle infinie
            const currentZip = values?.actualplace?.zipcode;
            const currentCity = values?.actualplace?.city;
            const newZip = selectedCity?.zip;
            const newCity = selectedCity?.name;

            // Si les valeurs sont identiques, ne rien faire
            if (currentZip === newZip && currentCity === newCity) {
                return;
            }

            const newValues = {
                ...values, actualplace: {
                    ...values?.actualplace,
                    zipcode: newZip,
                    city: newCity
                }
            };

            setValues(newValues);

            if (pathname?.includes("nouveau")) {
                setNewForm(prevForm => ({ ...prevForm, ...newValues }));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCity?.city_zip, pathname]);

    const onChangeActualPlace = (e) => {
        const newValues = { ...values, actualplace: { ...values?.actualplace, [e.target.name]: e.target.value } };
        setValues(newValues);

        if (pathname?.includes("nouveau")) {
            setNewForm(prevForm => ({ ...prevForm, ...newValues }));
        }

        validateFormInput(e);
    }

    const handleFileChange = async (e) => {
        let selectedFile = e.target.files[0];
        if (e && selectedFile) {
            setFileName(selectedFile.name);
            const reader = new FileReader();
            reader.onloadend = () => {
                setSelectedImage(reader.result);
            };
            reader.readAsDataURL(selectedFile);

            try {
                const base64Photo = await fileToBase64(selectedFile);
                setSelectedCompressedFile(base64Photo);
                setSelectedImage(base64Photo);
                setSelectedPortrait(null);
                setUseDefaultPortrait(false); // Décocher la case
                const newValues = {
                    ...values,
                    portrait: selectedFile?.name,
                    url: "",
                    useGalleryPortrait: false
                };
                setValues(newValues);

                if (pathname?.includes("nouveau")) {
                    setNewForm({
                        ...newForm,
                        file: base64Photo,
                        image: base64Photo,
                        ...newValues,
                        file_name: selectedFile.name
                    });
                }
            } catch (error) {
                console.error("Erreur lors de la conversion en base64 : ", error);
            }
        }
    };

    const handleLibFileSelected = (url, imagePath = null) => {
        if (selectedPortrait === url) {
            setSelectedPortrait(null);
            setSelectedImage(null);
            const newValues = { ...values, url: "", useGalleryPortrait: false, portrait: "" };
            setValues(newValues);

            if (pathname?.includes("nouveau")) {
                setNewForm({ ...newForm, ...newValues, file: null, file_name: null });
            }
        } else {
            setSelectedPortrait(url);
            const displayPath = imagePath || `${IMG_FOLDER}/img/deceased_library/${url}`;
            setSelectedImage(displayPath);
            setSelectedCompressedFile(null);
            setFileName("");
            setUseDefaultPortrait(false); // Décocher la case
            const newValues = { ...values, url: url, useGalleryPortrait: true, portrait: url };
            setValues(newValues);

            if (pathname?.includes("nouveau")) {
                setNewForm({ ...newForm, ...newValues, file: null, file_name: null, image: displayPath });
            }
        }
    }

    useEffect(() => {
        if (imageLib && imageLib.length > 0) {
            const enabledImages = imageLib.filter(image => image?.enabled && image?.category === 1);
            const combined = [];

            // Images de l'agence affichées en priorité, avant les images communes (admin).
            enabledImages.forEach(image => {
                combined.push({
                    ...image,
                    path: `${IMG_FOLDER}/img/deceased_library/${agencyId}/${image?.url}`,
                    source: 'agency',
                    uniqueKey: `agency-${agencyId}-${image.id}`
                });
            });
            enabledImages.forEach(image => {
                combined.push({
                    ...image,
                    path: `${IMG_FOLDER}/img/deceased_library/${image?.url}`,
                    source: 'root',
                    uniqueKey: `root-${image.id}`
                });
            });

            setCombinedImageList(combined);
        }
    }, [imageLib, agencyId, IMG_FOLDER]);

    useEffect(() => {
        if (deceasedInfos?.portrait === "" || !deceasedInfos?.portrait) {
            setUseDefaultPortrait(true);
            // Si portrait est vide, afficher l'image par défaut de l'agence. isAgencySpecific
            // (calculé côté back via file_exists) indique le dossier réel, sans quoi une image
            // propre à l'agence (absente du dossier racine partagé) ne s'affichait pas.
            if (agencyDefaultDeceasedImage?.defaultImage) {
                setSelectedImage(agencyDefaultDeceasedImage?.isAgencySpecific
                    ? `${IMG_FOLDER}/img/deceased_library/${agencyId}/${agencyDefaultDeceasedImage.defaultImage}`
                    : `${IMG_FOLDER}/img/deceased_library/${agencyDefaultDeceasedImage.defaultImage}`);
            }
        } else {
            setUseDefaultPortrait(false);
            if (deceasedInfos?.useGalleryPortrait && deceasedInfos?.portrait) {
                setSelectedPortrait(deceasedInfos.portrait);

                const agencyImagePath = `${IMG_FOLDER}/img/deceased_library/${agencyId}/${deceasedInfos.portrait}`;
                const rootImagePath = `${IMG_FOLDER}/img/deceased_library/${deceasedInfos.portrait}`;

                const img = new Image();
                img.onload = () => {
                    setSelectedImage(agencyImagePath);
                };
                img.onerror = () => {
                    setSelectedImage(rootImagePath);
                };
                img.src = agencyImagePath;
            } else if (deceasedInfos?.portrait && deceasedInfos?.uploadDir) {
                setSelectedImage(`${IMG_FOLDER}/img/${deceasedInfos?.uploadDir}/${deceasedInfos?.portrait}`);
            }
        }
    }, [deceasedInfos, agencyId, IMG_FOLDER, agencyDefaultDeceasedImage]);

    useEffect(() => {
        if (pathname?.includes("nouveau")) {
            if (newForm?.portrait === "" || !newForm?.portrait) {
                setUseDefaultPortrait(true);
                // Si portrait est vide, afficher l'image par défaut de l'agence
                if (agencyDefaultDeceasedImage?.defaultImage) {
                    setSelectedImage(agencyDefaultDeceasedImage?.isAgencySpecific
                        ? `${IMG_FOLDER}/img/deceased_library/${agencyId}/${agencyDefaultDeceasedImage.defaultImage}`
                        : `${IMG_FOLDER}/img/deceased_library/${agencyDefaultDeceasedImage.defaultImage}`);
                }
            } else {
                setUseDefaultPortrait(false);
                if (newForm?.useGalleryPortrait && newForm?.portrait) {
                    setSelectedPortrait(newForm.portrait);
                    setSelectedImage(newForm.image || `${IMG_FOLDER}/img/deceased_library/${newForm.portrait}`);
                } else if (newForm?.image) {
                    setSelectedImage(newForm.image);
                }
            }
        }
    }, [newForm, pathname, IMG_FOLDER, deceasedInfos, agencyId, agencyDefaultDeceasedImage]);

    const handleRemoveModal = () => {
        setShowModal(false)
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!checkRequiredFields()) {
            return;
        }
        let body;
        if (!pathname?.includes("nouveau")) {
            body = {
                deceasedId: id,
                agencyId: agencyId,
                birthplaceId: values || 0,
                title: values?.title,
                civility: values?.civility || "M",
                hiddenCivility: values?.hiddenCivility || false,
                lastName: values?.lastName?.trim(),
                firstName: values?.firstName?.trim(),
                birthName: values?.birthName?.trim(),
                actualplace: {
                    addressId: values?.actualplace?.addressId,
                    address: values?.actualplace?.address,
                    addressComp: values?.actualplace?.addressComp,
                    zipcode: values?.actualplace?.zipcode,
                    city: values?.actualplace?.city,
                    country: values?.actualplace?.country ? values?.actualplace?.country : "FR"
                }
            };
        } else {
            body = {
                deceasedId: 0,
                agencyId: agencyId,
                title: values?.title,
                civility: values?.civility || "M",
                hiddenCivility: values?.hiddenCivility || false,
                lastName: values?.lastName?.trim(),
                firstName: values?.firstName?.trim(),
                birthName: values?.birthName?.trim(),
                isListed: values?.isListed,
                portrait: values?.portrait,
                useGalleryPortrait: values?.useGalleryPortrait || false,
                file: selectedCompressedFile,
                file_name: fileName,
                image: selectedImage,
                url: values?.url,
                actualplace: {
                    addressId: values?.actualplace?.addressId,
                    address: values?.actualplace?.address,
                    addressComp: values?.actualplace?.addressComp,
                    zipcode: normalizeZipCode(values?.actualplace?.zipcode),
                    city: values?.actualplace?.city,
                    country: values?.actualplace?.country ? values?.actualplace?.country : "FR"
                }
            }
        }
        if (!pathname?.includes("nouveau")) {
            await updateDeceasedGeneralInfos(token, body)
                .then(response => {
                    removeErrorList()
                    if (!response?.response?.data?.error) {
                        setShowModal(false)
                        apiDeceasedInfos()
                    } else {
                        getErrorListFromValidation(response?.response?.data?.error)
                    }
                })
        } else {
            setNewForm({ ...newForm, ...body })
            setShowModal(false)
            setSelectedCity()
        }
        setSelectedCity(null);
    }

    return (
        <>
            <fieldset>
                {(legend !== "no-legend") && <legend>{legend}</legend>}
                    <div className="form-row">
                        {/*<div className="form-input">
                            <label htmlFor="title">Titre de civilité</label>
                            <input type="text" name="title"
                                   placeholder="ex: Docteur, Professeur..."
                                   defaultValue={values?.title}
                                   onChange={onChange}>
                            </input>
                        </div>*/}
                        <CivilityForm required="required" onChange={onChange} value={values?.civility} />
                        {(legend === "Informations du défunt" || pathname.includes("nouveau")) &&
                            <div className="form-input">
                                <label htmlFor="hiddenCivility">Masquer la civilité <span>(masquer s'il s'agit d'un enfant)</span></label>
                                <input
                                    type="checkbox"
                                    name="hiddenCivility"
                                    defaultChecked={values?.hiddenCivility}
                                    onChange={onChange}
                                />
                            </div>
                        }
                    </div>

                <div className="form-row">
                    <LastNameForm required={true} onChange={onChange} value={values?.lastName} />
                    {values?.civility != "M" &&
                        <div className="form-input">
                            <label htmlFor="birthName">Nom de naissance </label>
                            <input
                                placeholder="Nom de naissance"
                                type="text"
                                name="birthName"
                                defaultValue={values?.birthName}
                                pattern={patternListChoices?.lastname}
                                onChange={(e) => { e.target.value = e.target.value.toUpperCase(); onChange(e); }} />
                        </div>
                    }
                    <FirstnameForm required={true} onChange={onChange} value={values?.firstName} />
                </div>

                {showPortraitModal && (
                    <div className="modal-overlay modal-overlay-secondary">
                        <div className="modal-content modal-portrait-unified" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h3>
                                    <i className="fa fa-image"></i>
                                    Modifier le portrait
                                </h3>
                                <i className="fa fa-times close-button" onClick={() => setShowPortraitModal(false)}></i>
                            </div>
                            <div className="modal-body modal-body-portrait">
                                {/* Section gauche: Bibliothèque */}
                                <div className="portrait-section portrait-library-section">
                                    <h4>
                                        <i className="fa fa-images"></i>
                                        Bibliothèque d'images
                                    </h4>
                                    <div className="library-images-container">
                                        <div className="library-images-grid">
                                            {combinedImageList?.map((image) => (
                                                <div
                                                    key={image.uniqueKey}
                                                    className={`library-image-item ${selectedPortrait === image?.url ? "selected" : ""}`}
                                                    onClick={() => handleLibFileSelected(image?.url, image?.path)}
                                                >
                                                    {selectedPortrait === image?.url && (
                                                        <i className="fa fa-check-circle" />
                                                    )}
                                                    <img
                                                        src={image.path}
                                                        alt="Portrait du défunt"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.parentElement.style.display = 'none';
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                        {combinedImageList?.length === 0 && (
                                            <p className="no-images">Aucune image disponible dans la bibliothèque</p>
                                        )}
                                    </div>
                                </div>

                                {/* Section droite: Import */}
                                <div className="portrait-section portrait-upload-section">
                                    <h4>
                                        <i className="fa fa-upload"></i>
                                        Importer une image
                                    </h4>
                                    <div className="upload-content">
                                        {!selectedImage ? (
                                            <label htmlFor="portrait-general" className="upload-label">
                                                <i className="fa fa-cloud-upload-alt"></i>
                                                <span>Sélectionner une image</span>
                                            </label>
                                        ) : (
                                            <div className="upload-preview-compact">
                                                <img src={selectedImage} alt="Aperçu" />
                                                <div className="preview-info">
                                                    <i className="fa fa-check-circle"></i>
                                                    <p className="file-name">{fileName}</p>
                                                    <button
                                                        type="button"
                                                        className="change-image-btn"
                                                        onClick={() => {
                                                            setSelectedImage(null);
                                                            setSelectedCompressedFile(null);
                                                            setFileName("");
                                                        }}
                                                    >
                                                        <i className="fa fa-sync-alt"></i>
                                                        Changer
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        <input
                                            type="file"
                                            name="portrait"
                                            accept="image/*"
                                            id="portrait-general"
                                            onChange={handleFileChange}
                                            className="hidden"
                                        />
                                    </div>
                                    <div className="portrait-upload-actions">
                                        <button type="button" className="red-button" onClick={() => setShowPortraitModal(false)}>
                                            <i className="fa fa-times"></i>
                                            <span>Annuler</span>
                                        </button>
                                        <button type="button" className="green-button" onClick={() => setShowPortraitModal(false)}>
                                            <i className="fa fa-check"></i>
                                            <span>Valider</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <InputFullAddress
                    zipField="zipcode"
                    cityField="city"
                    addressField="address"
                    compField="addressComp"
                    countryField="country"
                    address={values?.actualplace?.address}
                    comp={values?.actualplace?.addressComp}
                    city={values?.actualplace?.city}
                    zipcode={values?.actualplace?.zipcode}
                    country={values?.actualplace?.country || "FR"}
                    required={true}
                    onChange={onChangeActualPlace}
                    type="actual"
                />

                {!pathname?.includes("nouveau") && !hideButtons && (
                    <>
                        <FormButtons handleRemoveModal={handleRemoveModal} handleSubmit={handleSubmit} />
                        <div className="errors"></div>
                    </>
                )}
                <div className="portrait-group">
                    <div className="portrait-container portrait-deceased">
                        <label className="portrait-label center">
                            Portrait du défunt
                        </label>

                        <div className="portrait-frame">
                            {selectedImage ? (
                                <img
                                    src={selectedImage}
                                    alt="Image sélectionnée"
                                />
                            ) : values?.portrait === "" || !values?.portrait ? (
                                // Si portrait est vide, afficher l'image par défaut de l'agence
                                agencyDefaultDeceasedImage?.defaultImage ? (
                                    <img
                                        src={agencyDefaultDeceasedImage?.isAgencySpecific
                                            ? `${IMG_FOLDER}/img/deceased_library/${agencyId}/${agencyDefaultDeceasedImage.defaultImage}`
                                            : `${IMG_FOLDER}/img/deceased_library/${agencyDefaultDeceasedImage.defaultImage}`}
                                        alt="Portrait par défaut"
                                    />
                                ) : (
                                    <span className="portrait-placeholder">
                                        Aucune image
                                    </span>
                                )
                            ) : deceasedInfos?.useGalleryPortrait && values?.portrait ? (
                                <img
                                    src={`${IMG_FOLDER}/img/deceased_library/${agencyId}/${values?.portrait}`}
                                    alt="Portrait de la bibliothèque"
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = `${IMG_FOLDER}/img/deceased_library/${values?.portrait}`;
                                    }}
                                />
                            ) : values?.portrait && deceasedInfos?.uploadDir ? (
                                <img
                                    src={`${IMG_FOLDER}/img/${deceasedInfos?.uploadDir}/${values?.portrait}`}
                                    alt="Portrait du défunt"
                                />
                            ) : (
                                <span className="portrait-placeholder">
                                    Aucune image
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="portrait-actions">
                        <button
                            type="button"
                            onClick={() => setShowPortraitModal(true)}
                            className="portrait-modify-btn"
                        >
                            <span>Accéder à la bibliothèque</span>
                        </button>

                        <div className="portrait-default-checkbox">
                            <input
                                type="checkbox"
                                id="useDefaultPortrait"
                                name="useDefaultPortrait"
                                checked={useDefaultPortrait}
                                onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    setUseDefaultPortrait(isChecked);

                                    if (isChecked) {
                                        // Utiliser l'image par défaut de l'agence : le fichier vit dans
                                        // deceased_library (useGalleryPortrait doit donc être true), pas
                                        // dans le dossier d'upload du défunt. Envoyer "" + false ici forçait
                                        // le back à deviner/corriger ces valeurs après coup.
                                        const defaultImage = agencyDefaultDeceasedImage?.defaultImage || "";
                                        const newValues = {
                                            ...values,
                                            portrait: defaultImage,
                                            useGalleryPortrait: !!defaultImage,
                                            url: "",
                                            file: null,
                                            file_name: null
                                        };
                                        setValues(newValues);

                                        if (pathname?.includes("nouveau")) {
                                            setNewForm({
                                                ...newForm,
                                                portrait: defaultImage,
                                                useGalleryPortrait: !!defaultImage,
                                                url: "",
                                                file: null,
                                                file_name: null,
                                                image: null
                                            });
                                        }

                                        setSelectedPortrait(null);
                                        setSelectedCompressedFile(null);
                                        setFileName("");

                                        // Afficher l'image par défaut directement
                                        if (agencyDefaultDeceasedImage?.defaultImage) {
                                            setSelectedImage(agencyDefaultDeceasedImage?.isAgencySpecific
                                                ? `${IMG_FOLDER}/img/deceased_library/${agencyId}/${agencyDefaultDeceasedImage.defaultImage}`
                                                : `${IMG_FOLDER}/img/deceased_library/${agencyDefaultDeceasedImage.defaultImage}`);
                                        } else {
                                            setSelectedImage(null);
                                        }
                                    } else {
                                        // Quand on décoche, on réinitialise seulement l'affichage, pas le portrait
                                        setSelectedImage(null);
                                        setSelectedPortrait(null);
                                        setSelectedCompressedFile(null);
                                        setFileName("");
                                    }
                                }}
                            />
                            <label htmlFor="useDefaultPortrait">Utiliser l'image par défaut</label>
                        </div>
                    </div>
                </div>
            </fieldset>
        </>
    )
}


